import { ExerciseResponse } from '@src/data/exercise-response'
import { DEFAULT_DURATION_HOME_EXERCISE } from '@src/util/constants'
import ClubModel from '@src/data/club'
import { WorkoutType } from '@src/data/workout-data'
import camelcaseKeys from 'camelcase-keys'

export type EquipmentWithWeight = { name: string, weight: number, weightUnit: string }
export class WorkoutExerciseConfig {

  repetitionDuration: number
  repetitions: number | null = null
  side: string | null = ''
  subsequentRestPeriod: number | null
  weight: number | null

  constructor (attributes?) {
    Object.assign(this, attributes)
    if (!this.side) {
      this.side = ''
    }
  }

  setDefaultValues (exercise: ExerciseResponse) {
    if (exercise) {
      if (!this.weight) {
        this.weight = exercise.getDefaultWeight()
      }
      if (!this.repetitionDuration) {
        this.repetitionDuration = exercise.durationMedium
      }
      if (!this.subsequentRestPeriod) {
        this.subsequentRestPeriod = exercise.subsequentRestPeriod
      }
    }
  }

  /**
   * Either return the set repetitions count, or compute the repetitions count out of the repetition duration and the club exercise execution time.
   */
  getRepetitionsCount (screenExerciseLen: number) {
    return this.repetitions || this.getDefaultRepetitionsCount(screenExerciseLen)
  }

  getDefaultRepetitionsCount (screenExerciseLen: number) {
    return screenExerciseLen / this.repetitionDuration
  }

  /**
   * Subsequent rest period (pause) between exercises in a continuous/non-continuous workout: either a set value or a default value.
   */
  getSubsequentRestPeriod (workoutType: WorkoutType, club: ClubModel) {
    if (workoutType === WorkoutType.HOME) {
      return 15
    }
    return this.subsequentRestPeriod || club.screenSummaryLen
  }
}

export class WorkoutExercise {

  id: number | null
  exerciseId: number | null
  workoutId: number
  stationId: number
  position: number
  repetitions: number | null = null
  repetitionDuration: number
  weight: number
  side: string
  subsequentRestPeriod: number | undefined
  exercise: ExerciseResponse
  timingSource: string = 'default'
  // the exercise before configuring it inside the wo builder
  initialExerciseConfig: WorkoutExerciseConfig | null = null
  breakpointAfterExercise: boolean = false
  equipmentWithWeight: EquipmentWithWeight[]

  constructor (attributes?) {
    Object.assign(this, attributes)
    if (!this.side) {
      this.side = ''
    }
    if (attributes && attributes.equipmentWithWeight) {
      this.equipmentWithWeight = attributes.equipmentWithWeight.map((item) => camelcaseKeys(item))
    }
  }

  setExercise (exercise: ExerciseResponse) {
    this.exercise = exercise
    if (exercise) {
      this.exerciseId = exercise.id
      if (!this.weight) {
        this.weight = exercise.getDefaultWeight()
      }

      if (!this.repetitionDuration) {
        this.repetitionDuration = exercise.durationMedium
      }

      if (!this.subsequentRestPeriod) {
        this.subsequentRestPeriod = exercise.subsequentRestPeriod
      }
    }
  }

  /**
   * Either return the set repetitions count, or compute the repetitions count out of the repetition duration and the club exercise execution time.
   */
  getRepetitionsCount (screenExerciseLen: number) {
    return this.repetitions || this.getDefaultRepetitionsCount(screenExerciseLen)
  }

  getDefaultRepetitionsCount (screenExerciseLen: number) {
    return screenExerciseLen / this.repetitionDuration
  }

  /**
   * Subsequent rest period (pause) between exercises in a continuous/non-continuous workout: either a set value or a default value.
   */
  getSubsequentRestPeriod (workoutType: WorkoutType, club: ClubModel) {
    if (workoutType === WorkoutType.HOME) {
      return 15
    }
    return this.subsequentRestPeriod || club.screenSummaryLen
  }

  /**
   * Compute exercise duration (based on the workout type).
   */
  getExerciseDuration (club: ClubModel, workoutType: WorkoutType, isCircleWorkout: boolean) {
    if (workoutType === 'home') {
      // duration in seconds
      return DEFAULT_DURATION_HOME_EXERCISE
    } else {
      if (isCircleWorkout) {
        return this.getCircleExerciseDuration(club)
      } else {
        return this.getContinuousExerciseDuration(club)
      }
    }
  }

  /**
   * Exercise duration in continuous/non-continuous workout is build considering the repetition duration, the repetitions count and the club preparation and results times.
   */
  getContinuousExerciseDuration (club: ClubModel) {
    return this.repetitionDuration * this.getRepetitionsCount(club.screenExerciseLen) + club.screenCountdownLen + this.getPauseDuration(club)
  }

  /**
   * Exercise duration in a circle configuration is standard for all (based on the club settings).
   */
  getCircleExerciseDuration (club: ClubModel) {
    return club.screenCountdownLen + club.screenExerciseLen + this.getPauseDuration(club)
  }

  getPauseDuration (club) {
    return Number(this.subsequentRestPeriod || club.screenSummaryLen)
  }

}
